import { z } from 'zod/v4';
import { stringToBooleanSchema } from '../common/index';

// ---------------------------------------------------------------------------
// Lead Status Config schemas
// ---------------------------------------------------------------------------

const leadStatusActionSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('send_notification'), templateId: z.number() }),
  z.object({ type: z.literal('assign_tag'), tagId: z.number() }),
  z.object({ type: z.literal('remove_tag'), tagId: z.number() }),
  z.object({
    type: z.literal('webhook'),
    url: z.string().url(),
    method: z.enum(['GET', 'POST']),
    headers: z.record(z.string(), z.string()).optional(),
  }),
]);

export const createLeadStatusConfigSchema = z.object({
  leadFunnelId: z.number().int().positive('Воронка обязательна'),
  name: z.string().min(1, 'Название обязательно').max(100, 'Название слишком длинное'),
  description: z.string().max(500, 'Описание слишком длинное').optional(),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Неверный формат цвета').optional().default('#6b7280'),
  position: z.number().int().min(0).optional().default(0),
  isInitial: z.boolean().optional().default(false),
  isFinal: z.boolean().optional().default(false),
  onEnterActions: z.array(leadStatusActionSchema).optional().default([]),
  onExitActions: z.array(leadStatusActionSchema).optional().default([]),
});

export const updateLeadStatusConfigSchema = createLeadStatusConfigSchema.partial();

export const leadStatusConfigFiltersSchema = z.object({
  search: z.string().optional(),
  isSystem: stringToBooleanSchema,
  isInitial: stringToBooleanSchema,
  isFinal: stringToBooleanSchema,
});

export type CreateLeadStatusConfigInput = z.infer<typeof createLeadStatusConfigSchema>;
export type UpdateLeadStatusConfigInput = z.infer<typeof updateLeadStatusConfigSchema>;
export type LeadStatusConfigFiltersInput = z.infer<typeof leadStatusConfigFiltersSchema>;

// ---------------------------------------------------------------------------
// Lead Status Transition schemas
// ---------------------------------------------------------------------------

export const createLeadStatusTransitionSchema = z.object({
  fromStatusId: z.number().int().positive().nullable().optional(),
  toStatusId: z.number().int().positive(),
  requireNote: z.boolean().optional().default(false),
  requireConfirmation: z.boolean().optional().default(false),
});

export type CreateLeadStatusTransitionInput = z.infer<typeof createLeadStatusTransitionSchema>;

// ---------------------------------------------------------------------------
// Lead Webhook schemas
// ---------------------------------------------------------------------------

export const createLeadWebhookSchema = z.object({
  name: z.string().min(1, 'Название обязательно').max(150, 'Название слишком длинное'),
  defaultTagIds: z.array(z.number().int().positive()).optional().default([]),
  fieldMapping: z.record(z.string(), z.string()).optional().default({}),
});

export const updateLeadWebhookSchema = z.object({
  name: z.string().min(1, 'Название обязательно').max(150, 'Название слишком длинное').optional(),
  defaultTagIds: z.array(z.number().int().positive()).optional(),
  fieldMapping: z.record(z.string(), z.string()).optional(),
  isActive: z.boolean().optional(),
});

export type CreateLeadWebhookInput = z.infer<typeof createLeadWebhookSchema>;
export type UpdateLeadWebhookInput = z.infer<typeof updateLeadWebhookSchema>;

// ---------------------------------------------------------------------------
// Lead Custom Field schemas
// ---------------------------------------------------------------------------

const leadCustomFieldTypeEnum = z.enum(['text', 'number', 'date', 'boolean']);

export const createLeadCustomFieldSchema = z.object({
  leadWebhookId: z.number().int().positive(),
  name: z.string()
    .min(1, 'Машинное имя обязательно')
    .max(50, 'Машинное имя слишком длинное')
    .regex(/^[a-z0-9_]+$/, 'Машинное имя может содержать только строчные буквы, цифры и подчёркивания'),
  label: z.string().min(1, 'Отображаемое название обязательно').max(100, 'Отображаемое название слишком длинное'),
  fieldType: leadCustomFieldTypeEnum,
  isRequired: z.boolean().optional().default(false),
  position: z.number().int().min(0).optional().default(0),
});

export const updateLeadCustomFieldSchema = z.object({
  label: z.string().min(1).max(100).optional(),
  fieldType: leadCustomFieldTypeEnum.optional(),
  isRequired: z.boolean().optional(),
  position: z.number().int().min(0).optional(),
});

export type CreateLeadCustomFieldInput = z.infer<typeof createLeadCustomFieldSchema>;
export type UpdateLeadCustomFieldInput = z.infer<typeof updateLeadCustomFieldSchema>;

// ---------------------------------------------------------------------------
// Lead schemas (admin manual creation and management)
// ---------------------------------------------------------------------------

export const createLeadSchema = z.object({
  statusConfigId: z.number().int().positive('ID конфигурации статуса должен быть положительным'),
  leadWebhookId: z.number().int().positive().optional(),
  email: z.string().email('Неверный формат email').optional(),
  phone: z.string().min(1).max(50).optional(),
  telegramUsername: z.string().min(1).max(100).optional(),
  firstName: z.string().min(1).max(100).optional(),
  lastName: z.string().min(1).max(100).optional(),
  customData: z.record(z.string(), z.unknown()).optional(),
});

export const updateLeadSchema = z.object({
  statusConfigId: z.number().int().positive().optional(),
  assignedAdminId: z.number().int().positive().nullable().optional(),
  email: z.string().email('Неверный формат email').nullable().optional(),
  phone: z.string().min(1).max(50).nullable().optional(),
  telegramUsername: z.string().min(1).max(100).nullable().optional(),
  firstName: z.string().min(1).max(100).nullable().optional(),
  lastName: z.string().min(1).max(100).nullable().optional(),
  customData: z.record(z.string(), z.unknown()).nullable().optional(),
});

export const leadFiltersSchema = z.object({
  statusConfigId: z.coerce.number().int().positive().optional(),
  assignedAdminId: z.coerce.number().int().positive().optional(),
  leadWebhookId: z.coerce.number().int().positive().optional(),
  search: z.string().optional(),
  hasUserId: stringToBooleanSchema,
  tagIds: z.string().transform((v) => v.split(',').map(Number).filter(Boolean)).optional(),
  createdFrom: z.coerce.date().optional(),
  createdTo: z.coerce.date().optional(),
  sortBy: z.enum(['id', 'createdAt', 'updatedAt', 'statusConfigId', 'assignedAdminId']).optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
  showDeleted: stringToBooleanSchema,
});

export type CreateLeadInput = z.infer<typeof createLeadSchema>;
export type UpdateLeadInput = z.infer<typeof updateLeadSchema>;
export type LeadFiltersInput = z.infer<typeof leadFiltersSchema>;

// ---------------------------------------------------------------------------
// Lead status change
// ---------------------------------------------------------------------------

export const changeLeadStatusSchema = z.object({
  statusConfigId: z.number().int().positive('ID статуса должен быть положительным'),
  note: z.string().max(2000, 'Заметка слишком длинная (максимум 2000 символов)').optional(),
});

export type ChangeLeadStatusInput = z.infer<typeof changeLeadStatusSchema>;

// ---------------------------------------------------------------------------
// Lead tags
// ---------------------------------------------------------------------------

const leadTagSourceEnum = z.enum(['manual', 'utm_auto', 'webhook']);

export const assignLeadTagSchema = z.object({
  tagId: z.number().int().positive('ID тега должен быть положительным'),
  source: leadTagSourceEnum.optional().default('manual'),
});

export const removeLeadTagSchema = z.object({
  tagId: z.number().int().positive('ID тега должен быть положительным'),
});

export type AssignLeadTagInput = z.infer<typeof assignLeadTagSchema>;
export type RemoveLeadTagInput = z.infer<typeof removeLeadTagSchema>;

// ---------------------------------------------------------------------------
// Lead notes
// ---------------------------------------------------------------------------

export const createLeadNoteSchema = z.object({
  content: z.string()
    .min(1, 'Содержимое заметки не может быть пустым')
    .max(5000, 'Заметка слишком длинная (максимум 5000 символов)'),
});

export const updateLeadNoteSchema = z.object({
  content: z.string()
    .min(1, 'Содержимое заметки не может быть пустым')
    .max(5000, 'Заметка слишком длинная (максимум 5000 символов)'),
});

export type CreateLeadNoteInput = z.infer<typeof createLeadNoteSchema>;
export type UpdateLeadNoteInput = z.infer<typeof updateLeadNoteSchema>;

// ---------------------------------------------------------------------------
// Webhook payload (incoming external request)
// ---------------------------------------------------------------------------

export const webhookLeadPayloadSchema = z
  .object({
    // Optional UTM parameters — validated if present
    utm_source: z.string().min(1).max(200).optional(),
    utm_medium: z.string().min(1).max(200).optional(),
    utm_campaign: z.string().min(1).max(200).optional(),
    utm_term: z.string().min(1).max(200).optional(),
    utm_content: z.string().min(1).max(200).optional(),
    // Optional idempotency key
    external_id: z.string().min(1).max(255).optional(),
  })
  // Accept any additional fields from the webhook — they will be stored in metadata
  .passthrough();

export type WebhookLeadPayloadInput = z.infer<typeof webhookLeadPayloadSchema>;
